import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import dayjs from "dayjs";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback } from "react";
import * as Yup from "yup";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { useCreateIcmCellOverride, useClearIcmCellOverride } from "@/icm/query";
import type { IcmCellOverride, IcmLineItem } from "@/icm/types";
import { formatTraceValue, humanizeKey } from "@/icm/utils";

interface EditCellModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
  statementId?: string;
  lineItem: IcmLineItem;
  columnName: string;
  currentValue: unknown;
  existingOverride: IcmCellOverride | null;
}

interface EditCellFormValues {
  value: string;
  reason: string;
}

const validationSchema = Yup.object({
  value: Yup.string().required("Value is required"),
  reason: Yup.string().required("Reason is required"),
});

export const EditCellModal = ({
  open,
  onOpenChange,
  businessId,
  statementId,
  lineItem,
  columnName,
  currentValue,
  existingOverride,
}: EditCellModalProps) => {
  const { mutate: createOverride } = useCreateIcmCellOverride(
    businessId,
    statementId,
  );
  const { mutate: clearOverride, isPending: isClearPending } =
    useClearIcmCellOverride(businessId, statementId);

  const initialValues: EditCellFormValues = {
    value: existingOverride ? String(existingOverride.value) : "",
    reason: "",
  };
  const columnLabel = humanizeKey(columnName);

  const handleSubmit = useCallback(
    (
      values: EditCellFormValues,
      { setSubmitting }: FormikHelpers<EditCellFormValues>,
    ) => {
      createOverride(
        {
          payout_rule_id: lineItem.payout_rule_id,
          source_record_id: lineItem.source_record_id,
          column_name: columnName,
          value: values.value,
          reason: values.reason,
        },
        {
          onSuccess: () => {
            toast.success("Override saved");
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error("Failed to save override", {
              description: getErrorMessage(error),
            });
            setSubmitting(false);
          },
        },
      );
    },
    [createOverride, lineItem, columnName, onOpenChange],
  );

  const handleClearOverride = useCallback(() => {
    if (!existingOverride) return;
    clearOverride(
      { overrideId: existingOverride.id },
      {
        onSuccess: () => {
          toast.success("Override cleared");
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error("Failed to clear override", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  }, [existingOverride, clearOverride, onOpenChange]);

  const displayOriginalValue = formatTraceValue(
    columnName,
    existingOverride?.previous_value ?? currentValue,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogDescription className="sr-only">
          Edit the {columnLabel} value for this line item.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle>Edit {columnLabel}</DialogTitle>
        </DialogHeader>

        <Stack align="start" gap={4}>
          <Stack align="start" gap={1}>
            <Text variant="label" tone="muted">
              {existingOverride ? "Original Value" : "Current Value"}
            </Text>
            <Text>{displayOriginalValue}</Text>
          </Stack>

          {existingOverride && (
            <Stack align="start" gap={1}>
              <Text variant="label" tone="muted">
                Current override
              </Text>
              <Text>
                {formatTraceValue(columnName, existingOverride.value)}
              </Text>
              <Text variant="caption" tone="muted">
                Edited by {existingOverride.edited_by.full_name} on{" "}
                {dayjs(existingOverride.edited_at).format("MMM DD, YYYY")}
              </Text>
              {existingOverride.stale && (
                <Alert color="warning" role="status">
                  <WarningIcon />
                  <AlertDescription>
                    <Text>
                      This override may be stale. The underlying data has
                      changed.
                    </Text>
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
          )}
        </Stack>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount
        >
          {({ isValid, isSubmitting, getFieldProps }) => (
            <Form>
              <Stack align="start" gap={4} className="pb-4">
                <FormInput
                  label={`New ${columnLabel}`}
                  fullWidth
                  required
                  {...getFieldProps("value")}
                />
                <Stack gap={2} className="w-full">
                  <Label htmlFor="edit-cell-reason">
                    Reason <span aria-hidden>*</span>
                  </Label>
                  <Textarea
                    id="edit-cell-reason"
                    className="min-h-32"
                    placeholder="Reason *"
                    {...getFieldProps("reason")}
                  />
                </Stack>
              </Stack>

              <DialogFooter>
                {existingOverride && (
                  <Button
                    type="button"
                    variant="outline"
                    color="danger"
                    size="sm"
                    onClick={handleClearOverride}
                    disabled={isSubmitting || isClearPending}
                    className="mr-auto"
                    loading={isClearPending}
                  >
                    Clear override
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!isValid || isSubmitting || isClearPending}
                  loading={isSubmitting}
                >
                  Save override
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
